import { useEffect, useState, useMemo, useRef } from 'react';
import cx from 'classnames';
import orderBy from 'lodash/orderBy';
import { useRouter } from 'next/compat/router';
import ReactPaginate from 'react-paginate';
import { useDispatch } from 'react-redux';
import { useDidUpdate } from 'rooks';
import styled from 'styled-components';

import { SelectDropdown } from '@pass-frontend/common/components/select-dropdown/ui';
import { FACILITY_PATHS } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { SORT_CRITERIA } from '@pass-frontend/data-access/types';
import { eligibilityLookupSlice } from '@pass-frontend/features/facility-checkin/data-access';
import {
  MemberCheckModuleLoader,
  useCurrenEligibilityCheck,
  useFindMemberAccess,
} from '@pass-frontend/features/facility-checkin/data-bridge';
import { useGymsSearchByLocation } from '@pass-frontend/features/standalone/data-bridge';
import { StyledPagination } from '@pass-frontend/features/standalone/ui';
import { RdsIcon } from '@pass-frontend/rally-energon-react';
import { RESPONSIVE_QUERY } from '@pass-frontend/utility/style-helpers';

import { GymCard } from './gym-card';
import { FacilityHeader } from '../facility-header/facility-header';
import { LocationSearch } from '../location-search/location-search';
import { MemberDetails } from '../member-details/member-details';

const LocationResultsWrapper = styled.div`
  min-height: calc(100vh - 144px);
`;

const WidthConstrainer = styled.div`
  margin: 0 auto;
  max-width: 1272px;

  ${RESPONSIVE_QUERY.large} {
    width: 90%;
  }
`;

const WhiteBoxes = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: minmax(min-content, 840px) minmax(min-content, 408px);

  ${RESPONSIVE_QUERY.medium} {
    grid-template-columns: 1fr;
  }
`;

const GymSearchWrapper = styled.div`
  flex-direction: column;

  input {
    height: 40px;
    width: 384px;
  }
`;

const StyledDropdownWrapper = styled.div`
  align-self: flex-end;
  width: 160px;

  .select-dropdown__control.control {
    border-color: #007897;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const StyledGymsCount = styled.p`
  align-self: flex-end;
  font-weight: 500 !important;
  margin-bottom: 0;
`;

const StyledSortByWrapper = styled.div`
  margin-left: auto;

  label {
    align-self: center;
  }
`;

const StyledGymListWrapper = styled.div`
  border-top: 1px solid #ccc;
`;

const GYMS_PER_PAGE = 20;

const DISTANCE_OPTIONS = [
  { label: 'Within 10 mi', value: '10' },
  { label: 'Within 20 mi', value: '20' },
  { label: 'Within 30 mi', value: '30' },
  { label: 'Within 40 mi', value: '40' },
];

const SORT_BY_OPTIONS = [
  { label: 'Distance', value: SORT_CRITERIA.DISTANCE },
  { label: 'A-Z', value: SORT_CRITERIA.A_TO_Z },
];

interface IGymsFilter {
  name: string;
  distance: { label: string; value: string };
  sortBy: { label: string; value: string };
}

export function LocationSearchResults() {
  const { t } = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();

  const paginationRef = useRef<HTMLInputElement>(null);

  const {
    memberDetails,
    searchedLocationOption,
    searchedLocationList,
    appliedGymsFilter,
  } = useCurrenEligibilityCheck();

  const defaultDistanceOption =
    appliedGymsFilter?.distance ?? DISTANCE_OPTIONS[0];
  const defaultSortByOption = appliedGymsFilter?.sortBy ?? SORT_BY_OPTIONS[0];

  const [searchedGymName, setSearchedGymName] = useState(
    appliedGymsFilter?.name ?? '',
  );

  const [sortBy, setSortBy] = useState(defaultSortByOption.value);
  const [pageNumber, setPageNumber] = useState(0);

  const [gymsFilter, setGymsFilter] = useState<IGymsFilter>({
    name: searchedGymName,
    distance: defaultDistanceOption,
    sortBy: defaultSortByOption,
  });

  const { fetchLocationbasedGyms, locationBasedGyms } =
    useGymsSearchByLocation('');

  const { fetchaccess, hasAccess } = useFindMemberAccess();
  const searchedCoordinates = useMemo(
    () => ({
      longitude: searchedLocationOption?.value?.longitude,
      latitude: searchedLocationOption?.value?.latitude,
    }),
    [searchedLocationOption],
  );
  useEffect(() => {
    if (!memberDetails || !searchedLocationOption) {
      router?.push(FACILITY_PATHS.DASHBOARD);
    } else {
      fetchLocationbasedGyms(
        {
          ...searchedCoordinates,
          distance: Number(gymsFilter.distance.value),
        },
        memberDetails?.sector || '',
      );
    }
  }, [memberDetails, searchedLocationOption]);

  useEffect(() => {
    const listEls = paginationRef?.current?.querySelectorAll('li.break-link');
    if (listEls && listEls.length > 0) {
      listEls?.forEach((el: Element) => {
        const text = document.createElement('span');
        const ellipsis = document.createTextNode('...');
        text.appendChild(ellipsis);
        el.replaceChildren(text);
        (el as HTMLElement).tabIndex = -1;
      });
    }
  });

  useDidUpdate(() => {
    router?.push({
      pathname: FACILITY_PATHS.LOCATION_ACCESS,
      query: { hasAccess },
    });
  }, [hasAccess]);

  const fetchedLocationBasedGyms =
    locationBasedGyms?.locationBasedFacilityOfferings;

  const sortedGyms = useMemo(() => {
    // By default, gyms are sorted by the distance.
    if (sortBy === SORT_CRITERIA.DISTANCE) {
      return fetchedLocationBasedGyms;
    }
    return orderBy(fetchedLocationBasedGyms, [
      ({ name }) => name.toLowerCase(),
    ]);
  }, [fetchedLocationBasedGyms, sortBy]);

  const filteredGyms = useMemo(() => {
    if (searchedGymName === '') {
      return sortedGyms;
    }
    return sortedGyms?.filter(({ name }) =>
      name.toLowerCase().includes(searchedGymName.toLowerCase()),
    );
  }, [sortedGyms, searchedGymName]);

  const filteredGymsLength = filteredGyms ? filteredGyms.length : 0;
  const gymsVisited = pageNumber * GYMS_PER_PAGE;
  const pageCount = Math.ceil(filteredGymsLength / GYMS_PER_PAGE);
  const isPageCountFourOrLess = pageCount <= 4;

  const gymsToBeDisplayed = useMemo(
    () => filteredGyms?.slice(gymsVisited, gymsVisited + GYMS_PER_PAGE),
    [filteredGyms, gymsVisited],
  );

  const handleGymNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setGymsFilter((prevState) => ({
      ...prevState,
      name: val,
    }));
    setPageNumber(0);
    setSearchedGymName(val);
  };

  const handleDistanceChange = (option) => {
    const distance = Number(option.value);
    setPageNumber(0);
    setGymsFilter((prevState) => ({
      ...prevState,
      distance: option,
    }));
    fetchLocationbasedGyms(
      {
        ...searchedCoordinates,
        distance,
      },
      memberDetails?.sector || '',
    );
  };

  const handleSortByChange = (option) => {
    setGymsFilter((prevState) => ({
      ...prevState,
      sortBy: option,
    }));
    setSortBy(option.value);
  };

  const handleConfirmAccessClick = async (facilityId: string) => {
    dispatch(eligibilityLookupSlice.actions.setAppliedGymsFilter(gymsFilter));
    fetchaccess(
      facilityId,
      memberDetails?.tags,
      memberDetails?.product,
      memberDetails?.sector,
      memberDetails?.sponsorId,
      memberDetails?.isDependent,
      memberDetails?.tierGroupId,
    );
  };

  return (
    <>
      <FacilityHeader />
      <LocationResultsWrapper className="has-rds-bg-grey-lightest has-rds-pb-64">
        <WidthConstrainer className="has-rds-pv-40">
          <WhiteBoxes>
            <div>
              <div className="has-rds-mb-24">
                <LocationSearch
                  distance={Number(gymsFilter.distance.value)}
                  defaultLocationOption={searchedLocationOption}
                  defaultLocationList={searchedLocationList}
                />
              </div>
              <h6 className="has-rds-mb-16 is-rds-h6 rds-font-weight-medium">
                {t('facilityWeb.locationSearchResults.title')}
              </h6>
              <div className="large-box has-rds-bg-white has-rds-p-24 has-rds-radius-4 has-rds-box-shadow-neutral-spread-6">
                <p className="is-rds-body-3">
                  {t('facilityWeb.locationSearchResults.selectGym')}
                </p>
                <div className="is-flex has-rds-mb-24">
                  <GymSearchWrapper className="is-flex">
                    <label htmlFor="gym-name" className="rds-label">
                      {t('facilityWeb.locationSearchResults.gymName')}
                    </label>
                    <input
                      type="text"
                      id="gym-name"
                      value={searchedGymName}
                      onChange={handleGymNameChange}
                      className="has-rds-radius-4 has-rds-mt-8"
                    />
                  </GymSearchWrapper>
                  <StyledDropdownWrapper className="has-rds-ml-16">
                    <SelectDropdown
                      options={DISTANCE_OPTIONS}
                      defaultSelectedOption={defaultDistanceOption}
                      label={''}
                      onApply={handleDistanceChange}
                      isMulti={false}
                      blurInputOnSelect={false}
                    />
                  </StyledDropdownWrapper>
                </div>
                <div className="is-flex has-rds-mb-16">
                  <StyledGymsCount className="is-rds-micro">
                    {t(
                      filteredGymsLength === 1
                        ? 'facilityWeb.locationSearchResults.oneGym'
                        : 'facilityWeb.locationSearchResults.multipleGyms',
                      {
                        count: filteredGymsLength,
                      },
                    )}
                  </StyledGymsCount>
                  <StyledSortByWrapper className="is-flex">
                    <label className="rds-label">
                      {t('facilityWeb.locationSearchResults.sortBy')}
                    </label>
                    <StyledDropdownWrapper className="has-rds-ml-16">
                      <SelectDropdown
                        options={SORT_BY_OPTIONS}
                        defaultSelectedOption={defaultSortByOption}
                        label={''}
                        onApply={handleSortByChange}
                        isMulti={false}
                        blurInputOnSelect={false}
                      />
                    </StyledDropdownWrapper>
                  </StyledSortByWrapper>
                </div>
                {filteredGymsLength !== 0 && (
                  <StyledGymListWrapper className="has-rds-pt-16">
                    {gymsToBeDisplayed?.map((gym) => (
                      <GymCard
                        key={gym.facilityId}
                        gym={gym}
                        fromCoordinates={searchedCoordinates}
                        handleConfirmAccessClick={handleConfirmAccessClick}
                      />
                    ))}
                    <StyledPagination
                      ref={paginationRef}
                      className={cx({
                        'hide-controls': isPageCountFourOrLess,
                      })}
                    >
                      <ReactPaginate
                        previousLabel={
                          <RdsIcon
                            name="system-pagination-arrow-left"
                            size="16"
                          />
                        }
                        nextLabel={
                          <RdsIcon
                            name="system-pagination-arrow-right"
                            size="16"
                          />
                        }
                        breakClassName="break-link"
                        pageCount={pageCount}
                        onPageChange={({ selected }) => setPageNumber(selected)}
                        containerClassName={'paginationBtns'}
                        disabledClassName={'paginationDisabled'}
                        activeClassName={'paginationActive'}
                        pageRangeDisplayed={3}
                        marginPagesDisplayed={1}
                        forcePage={pageNumber}
                      />
                    </StyledPagination>
                  </StyledGymListWrapper>
                )}
              </div>
            </div>
            <div>
              <MemberCheckModuleLoader>
                <MemberDetails
                  memberDetails={memberDetails}
                  showNewSearchLink={true}
                />
              </MemberCheckModuleLoader>
            </div>
          </WhiteBoxes>
        </WidthConstrainer>
      </LocationResultsWrapper>
    </>
  );
}
